import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  List,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Avatar,
  AvatarGroup,
  Badge,
  TextField,
  IconButton,
  InputAdornment,
  Divider,
  Chip,
  Button,
  useTheme,
  useMediaQuery,
  Popover,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemAvatar,
  Snackbar,
  Select,
  FormControl,
  InputLabel,
  Drawer,
  Checkbox,
  ListItem,
} from '@mui/material';
import {
  Message as MessageIcon,
  Send as SendIcon,
  AttachFile as AttachIcon,
  EmojiEmotions as EmojiIcon,
  ArrowBack as BackIcon,
  MoreVert as MoreIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Forum as ForumIcon,
  Add as AddIcon,
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  SmartToy as BotIcon,
  PhotoAlbum as AlbumIcon,
  Description as DocIcon,
  PlayCircleOutline as PlayIcon,
  Reply as ReplyIcon,
  PersonAdd as PersonAddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client/react';
import { useSnackbar } from 'notistack';
import { format, isToday, isYesterday } from 'date-fns';
import Picker from '@emoji-mart/react';
import data from '@emoji-mart/data';
import { PageHeader, LoadingSpinner, SearchInput, MemberAvatar } from '../../components/common';
import { useChat } from '../../hooks/useChat';
import { useFamily, useAuth } from '../../context';
import { colors } from '../../theme';
import { useProfilePhotoUrl } from '../../hooks/useProfilePhotoUrl';
import { uploadChatFile } from '../../services/api/familypal/chatUpload';
import {
  getChatChatsByChatIdParticipants,
  postChatChatsByChatIdParticipants,
  deleteChatChatsByChatIdParticipantsByUserId,
} from '../../services/api/familypal';
import { GET_FAMILY, GET_ALBUMS, CREATE_ALBUM, SAVE_TO_ALBUM, INSERT_FAMILY_DATA } from '@common/graphql';
import { isBotMessage, FAMILYPAL_BOT_DISPLAY_NAME } from '@common/constants';

// Mention regex - matches @word at cursor position
const AT_MENTION_REGEX = /\B@([a-z0-9._-]*)$/i;

// Type for family member from GraphQL
interface FamilyMember {
  person_id: string;
  role: string;
  color?: string;
  person: {
    id: string;
    first_name: string;
    last_name: string;
    profile_photo_id?: string;
    user?: Array<{ id: string }>;
  };
}

// Type for pending file upload
interface PendingFile {
  id: string;
  file: File;
  preview?: string;
  progress: number;
  uploading: boolean;
  error?: string;
  uploadedId?: string;
}

// Mattermost-style avatar component for messages
const PostAvatar: React.FC<{
  profilePhotoId?: string;
  name: string;
  color: string;
}> = ({ profilePhotoId, name, color }) => {
  const thumbnailUrl = useProfilePhotoUrl(profilePhotoId, true);
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  return (
    <Avatar
      src={thumbnailUrl || undefined}
      sx={{
        width: 36,
        height: 36,
        bgcolor: color,
        fontSize: '0.875rem',
        fontWeight: 600,
      }}
    >
      {initials}
    </Avatar>
  );
};

// Bot avatar for AI assistant messages
const BotPostAvatar: React.FC = () => (
  <Avatar
    sx={{
      width: 36,
      height: 36,
      bgcolor: '#5C6BC0',
      fontSize: '0.875rem',
    }}
  >
    <BotIcon sx={{ fontSize: 20 }} />
  </Avatar>
);

const MessagesPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { currentFamily } = useFamily();

  // Query family data directly like mobile does
  const { data: familyData } = useQuery(GET_FAMILY);
  const familyMembers: FamilyMember[] = useMemo(() => {
    const families = (familyData as any)?.family || [];
    return families[0]?.members || [];
  }, [familyData]);

  // Helper to get user ID from family member
  const getUserId = (m: FamilyMember) => m.person?.user?.[0]?.id;

  // Build user ID to name map
  const memberNames = useMemo(() => {
    const names: Record<string, string> = {};
    familyMembers.forEach((m) => {
      const userId = getUserId(m);
      const fullName = `${m.person.first_name} ${m.person.last_name}`.trim();
      if (userId) {
        names[userId] = fullName;
      }
      names[m.person_id] = fullName;
    });
    return names;
  }, [familyMembers]);

  // Build user ID to profile photo ID map
  const memberPhotoIds = useMemo(() => {
    const photoIds: Record<string, string> = {};
    familyMembers.forEach((m) => {
      const userId = getUserId(m);
      if (userId && m.person.profile_photo_id) {
        photoIds[userId] = m.person.profile_photo_id;
      }
      if (m.person.profile_photo_id) {
        photoIds[m.person_id] = m.person.profile_photo_id;
      }
    });
    return photoIds;
  }, [familyMembers]);

  // Build user ID to color map
  const memberColors = useMemo(() => {
    const colorsMap: Record<string, string> = {};
    familyMembers.forEach((m) => {
      const userId = getUserId(m);
      const color = m.color || '#96D0F0';
      if (userId) {
        colorsMap[userId] = color;
      }
      colorsMap[m.person_id] = color;
    });
    return colorsMap;
  }, [familyMembers]);

  // Helper functions
  const getMemberName = (id: string): string => {
    if (!id) return 'Unknown';
    return memberNames[id] || 'Unknown';
  };
  const getMemberPhotoId = (id: string): string | undefined => {
    if (!id) return undefined;
    return memberPhotoIds[id];
  };
  const getMemberColor = (id: string): string => {
    if (!id) return '#96D0F0';
    return memberColors[id] || '#96D0F0';
  };

  // Chat state
  const {
    threads,
    threadsLoading,
    messages,
    messagesLoading,
    loadMoreMessages,
    loadingMoreMessages,
    hasMoreMessages,
    sendMessage,
    sending,
    selectThread,
    selectedThreadId,
    createFamilyChat,
  } = useChat(currentFamily?.id);
  const [creatingChat, setCreatingChat] = useState(false);

  // UI state
  const [messageInput, setMessageInput] = useState('');
  const [search, setSearch] = useState('');
  const [replyTo, setReplyTo] = useState<{ id: string; senderName: string; content: string } | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Emoji picker state
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLButtonElement | null>(null);
  const emojiPickerOpen = Boolean(emojiAnchorEl);

  // Context menu state for message attachments
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; message: any } | null>(null);
  const [albumPickerOpen, setAlbumPickerOpen] = useState(false);
  const [docSaveOpen, setDocSaveOpen] = useState(false);
  const [savedMessage, setSavedMessage] = useState<any>(null);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [docCategory, setDocCategory] = useState('other');

  // Members drawer state
  const [membersDrawerOpen, setMembersDrawerOpen] = useState(false);
  const [participants, setParticipants] = useState<any[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  const [addMembersMode, setAddMembersMode] = useState(false);
  const [selectedNewMembers, setSelectedNewMembers] = useState<string[]>([]);
  const [addingMembers, setAddingMembers] = useState(false);

  // Load older messages when scrolling to top
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el || loadingMoreMessages || !hasMoreMessages) return;
    // Trigger when within 200px of the top
    if (el.scrollTop < 200) {
      loadMoreMessages();
    }
  }, [loadMoreMessages, loadingMoreMessages, hasMoreMessages]);

  // Fetch participants when drawer opens
  const fetchParticipants = useCallback(async (chatId: string) => {
    setParticipantsLoading(true);
    try {
      const result = await getChatChatsByChatIdParticipants({
        path: { chat_id: chatId },
      } as any);
      const list = (result.data as any)?.participants || [];
      setParticipants(list);
    } catch (err) {
      console.error('Failed to fetch participants:', err);
    } finally {
      setParticipantsLoading(false);
    }
  }, []);

  const handleOpenMembersDrawer = useCallback(() => {
    if (!selectedThreadId) return;
    setMembersDrawerOpen(true);
    setAddMembersMode(false);
    setSelectedNewMembers([]);
    fetchParticipants(selectedThreadId);
  }, [selectedThreadId, fetchParticipants]);

  const handleCloseMembersDrawer = useCallback(() => {
    setMembersDrawerOpen(false);
    setAddMembersMode(false);
    setSelectedNewMembers([]);
  }, []);

  // Get current user's role in the chat
  const currentUserRole = useMemo(() => {
    const p = participants.find((p) => p.user_id === user?.id);
    return p?.role || 'member';
  }, [participants, user?.id]);

  const canManageMembers = currentUserRole === 'owner' || currentUserRole === 'admin';

  // Family members not yet in the chat
  const availableMembers = useMemo(() => {
    const participantUserIds = new Set(participants.map((p) => p.user_id));
    return familyMembers.filter((m) => {
      const userId = getUserId(m);
      return userId && !participantUserIds.has(userId);
    });
  }, [familyMembers, participants]);

  const handleAddMembers = useCallback(async () => {
    if (!selectedThreadId || selectedNewMembers.length === 0) return;
    setAddingMembers(true);
    try {
      await postChatChatsByChatIdParticipants({
        path: { chat_id: selectedThreadId },
        body: { user_ids: selectedNewMembers },
      } as any);
      enqueueSnackbar(`Added ${selectedNewMembers.length} member${selectedNewMembers.length > 1 ? 's' : ''}`, { variant: 'success' });
      setAddMembersMode(false);
      setSelectedNewMembers([]);
      fetchParticipants(selectedThreadId);
    } catch (err) {
      console.error('Failed to add members:', err);
      enqueueSnackbar('Failed to add members', { variant: 'error' });
    } finally {
      setAddingMembers(false);
    }
  }, [selectedThreadId, selectedNewMembers, fetchParticipants, enqueueSnackbar]);

  const handleRemoveParticipant = useCallback(async (userId: string) => {
    if (!selectedThreadId) return;
    const isSelf = userId === user?.id;
    const name = memberNames[userId] || 'Unknown';
    try {
      await deleteChatChatsByChatIdParticipantsByUserId({
        path: { chat_id: selectedThreadId, user_id: userId },
      } as any);
      enqueueSnackbar(isSelf ? 'You left the chat' : `Removed ${name}`, { variant: 'success' });
      if (isSelf) {
        handleCloseMembersDrawer();
        selectThread(null);
      } else {
        fetchParticipants(selectedThreadId);
      }
    } catch (err) {
      console.error('Failed to remove participant:', err);
      enqueueSnackbar('Failed to remove member', { variant: 'error' });
    }
  }, [selectedThreadId, user?.id, memberNames, fetchParticipants, selectThread, handleCloseMembersDrawer, enqueueSnackbar]);

  const toggleNewMember = useCallback((userId: string) => {
    setSelectedNewMembers((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  }, []);

  const { data: albumsData } = useQuery(GET_ALBUMS, { skip: !albumPickerOpen });
  const [createAlbumMutation] = useMutation(CREATE_ALBUM);
  const [saveToAlbumMutation] = useMutation(SAVE_TO_ALBUM);
  const [insertFamilyDataMutation] = useMutation(INSERT_FAMILY_DATA);

  const handleMessageContextMenu = useCallback((event: React.MouseEvent, message: any) => {
    const hasMedia = message.attachments?.some((a: any) => a.type === 'image' || a.type === 'video');
    const hasDocs = message.attachments?.some((a: any) => a.type === 'file');
    if (!hasMedia && !hasDocs) return;
    event.preventDefault();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, message });
  }, []);

  const handleSaveToAlbumClick = useCallback(() => {
    setSavedMessage(contextMenu?.message);
    setContextMenu(null);
    setAlbumPickerOpen(true);
  }, [contextMenu]);

  const handleSaveToDocumentsClick = useCallback(() => {
    setSavedMessage(contextMenu?.message);
    setContextMenu(null);
    setDocSaveOpen(true);
  }, [contextMenu]);

  const handleSaveToAlbum = useCallback(async (albumDataId: string) => {
    if (!savedMessage) return;
    const mediaAtts = savedMessage.attachments?.filter((a: any) => a.type === 'image' || a.type === 'video') || [];
    try {
      for (const att of mediaAtts) {
        await saveToAlbumMutation({
          variables: {
            data_id: albumDataId,
            type: att.type,
            uri: att.uri,
            bucket: att.bucket || null,
            key: att.key || null,
            name: att.name || null,
            size: att.size || null,
            mime_type: att.mimeType || att.mime_type || null,
            thumbnail: att.thumbnail || null,
            thumbnail_key: null,
            width: att.width || null,
            height: att.height || null,
            mini_preview: att.miniPreview || att.mini_preview || null,
            created_by: user?.id || '',
          },
        });
      }
      enqueueSnackbar(`${mediaAtts.length} item${mediaAtts.length > 1 ? 's' : ''} saved to album`, { variant: 'success' });
      setAlbumPickerOpen(false);
      setSavedMessage(null);
      setNewAlbumName('');
    } catch {
      enqueueSnackbar('Failed to save to album', { variant: 'error' });
    }
  }, [savedMessage, saveToAlbumMutation, user, enqueueSnackbar]);

  const handleCreateAlbumAndSave = useCallback(async () => {
    if (!newAlbumName.trim() || !currentFamily?.id) return;
    try {
      const result = await createAlbumMutation({
        variables: {
          family_id: currentFamily.id,
          data: { name: newAlbumName.trim(), fileIds: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
        },
      });
      const albumDataId = (result.data as any)?.insert_family_data_one?.data?.id;
      if (albumDataId) {
        await handleSaveToAlbum(albumDataId);
      }
    } catch {
      enqueueSnackbar('Failed to create album', { variant: 'error' });
    }
  }, [newAlbumName, currentFamily, createAlbumMutation, handleSaveToAlbum, enqueueSnackbar]);

  const handleSaveDocuments = useCallback(async () => {
    if (!savedMessage || !currentFamily?.id) return;
    const fileAtts = savedMessage.attachments?.filter((a: any) => a.type === 'file') || [];
    try {
      for (const att of fileAtts) {
        await insertFamilyDataMutation({
          variables: {
            object: {
              family_id: currentFamily.id,
              data: {
                data: {
                  type: 'document',
                  data: {
                    name: att.name || 'Untitled Document',
                    category: docCategory,
                    file_id: att.id,
                    file_name: att.name,
                    file_size: att.size,
                    mime_type: att.mimeType || att.mime_type,
                    source: 'chat',
                  },
                },
              },
            },
          },
        });
      }
      enqueueSnackbar(`${fileAtts.length} document${fileAtts.length > 1 ? 's' : ''} saved`, { variant: 'success' });
      setDocSaveOpen(false);
      setSavedMessage(null);
      setDocCategory('other');
    } catch {
      enqueueSnackbar('Failed to save document', { variant: 'error' });
    }
  }, [savedMessage, currentFamily, docCategory, insertFamilyDataMutation, enqueueSnackbar]);

  // File attachment state
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);

  // Mention autocomplete state
  const [mentionAnchorEl, setMentionAnchorEl] = useState<HTMLElement | null>(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const mentionPickerOpen = Boolean(mentionAnchorEl);

  // Get selected thread details
  const selectedThread = threads.find((t) => t.id === selectedThreadId);

  // Filter threads by search
  const filteredThreads = threads.filter((thread) => {
    if (!search) return true;
    const name = getThreadName(thread);
    return name.toLowerCase().includes(search.toLowerCase());
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Get user_id from participant
  function getParticipantUserId(p: any): string {
    if (typeof p === 'string') return p;
    return p?.user_id || '';
  }

  // Get thread display name
  function getThreadName(thread: any): string {
    if (thread.name) return thread.name;
    if (thread.type === 'family') return 'Family Chat';
    if (thread.type === 'direct') {
      const otherParticipant = thread.participants?.find((p: any) => getParticipantUserId(p) !== user?.id);
      const otherUserId = getParticipantUserId(otherParticipant);
      return otherUserId ? getMemberName(otherUserId) : 'Direct Message';
    }
    return 'Group Chat';
  }

  // Get thread participants
  function getThreadParticipants(thread: any) {
    if (thread.type === 'family') {
      return familyMembers.slice(0, 4).map(m => ({
        id: getUserId(m) || m.person_id,
      }));
    }
    if (thread.type === 'direct') {
      const other = thread.participants?.find((p: any) => getParticipantUserId(p) !== user?.id);
      const otherUserId = getParticipantUserId(other);
      return otherUserId ? [{ id: otherUserId }] : [];
    }
    return thread.participants?.slice(0, 4).map((p: any) => ({
      id: getParticipantUserId(p),
    })).filter((p: any) => p.id) || [];
  }

  // Format timestamp for thread list
  function formatTime(dateStr: string): string {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }
    if (isYesterday(date)) {
      return 'Yesterday';
    }
    return format(date, 'MMM d');
  }

  // Format message time (Mattermost style)
  function formatMessageTime(dateStr: string): string {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    }
    return format(date, 'MMM d, h:mm a');
  }

  // Handle create family chat
  const handleCreateFamilyChat = async () => {
    setCreatingChat(true);
    try {
      const newChat = await createFamilyChat();
      if (newChat) {
        selectThread(newChat.id);
        enqueueSnackbar('Family chat created', { variant: 'success' });
      }
    } catch (err) {
      enqueueSnackbar('Failed to create chat', { variant: 'error' });
    } finally {
      setCreatingChat(false);
    }
  };

  // Handle emoji picker
  const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setEmojiAnchorEl(event.currentTarget);
  };

  const handleEmojiClose = () => {
    setEmojiAnchorEl(null);
  };

  const handleEmojiSelect = (emoji: any) => {
    setMessageInput((prev) => prev + emoji.native);
    handleEmojiClose();
    inputRef.current?.focus();
  };

  // Handle file selection
  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newPendingFiles: PendingFile[] = Array.from(files).map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
      progress: 0,
      uploading: false,
    }));

    setPendingFiles((prev) => [...prev, ...newPendingFiles]);

    // Clear the input
    event.target.value = '';
  }, []);

  const handleRemoveFile = useCallback((fileId: string) => {
    setPendingFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  }, []);

  // Get mentionable members (family members with user IDs)
  const mentionableMembers = useMemo(() => {
    return familyMembers
      .filter((m) => getUserId(m))
      .map((m) => ({
        id: getUserId(m) || '',
        name: `${m.person.first_name} ${m.person.last_name}`.trim(),
        firstName: m.person.first_name,
      }));
  }, [familyMembers]);

  // Filter members by mention search
  const filteredMentionMembers = useMemo(() => {
    if (!mentionSearch) return mentionableMembers;
    const search = mentionSearch.toLowerCase();
    return mentionableMembers.filter(
      (m) => m.name.toLowerCase().includes(search) || m.firstName.toLowerCase().startsWith(search)
    );
  }, [mentionableMembers, mentionSearch]);

  // Handle input change - detect @mentions
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart || value.length;
    setMessageInput(value);

    // Check for @mention trigger
    const textBeforeCursor = value.substring(0, cursorPos);
    const match = textBeforeCursor.match(AT_MENTION_REGEX);

    if (match) {
      setMentionSearch(match[1]);
      setMentionCursorPosition(cursorPos);
      setMentionAnchorEl(e.target);
    } else {
      setMentionAnchorEl(null);
      setMentionSearch('');
    }
  }, []);

  // Handle selecting a mention from the popup
  const handleSelectMention = useCallback((member: { id: string; name: string; firstName: string }) => {
    const textBeforeCursor = messageInput.substring(0, mentionCursorPosition);
    const textAfterCursor = messageInput.substring(mentionCursorPosition);

    // Replace the @partial with @FirstName
    const newTextBefore = textBeforeCursor.replace(AT_MENTION_REGEX, `@${member.firstName} `);
    const newText = newTextBefore + textAfterCursor;

    setMessageInput(newText);
    setMentionAnchorEl(null);
    setMentionSearch('');

    // Focus back on input
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [messageInput, mentionCursorPosition]);

  // Close mention picker
  const handleCloseMentionPicker = useCallback(() => {
    setMentionAnchorEl(null);
    setMentionSearch('');
  }, []);

  // Parse mentions from text before sending
  const parseMentions = useCallback((text: string) => {
    const mentions: Array<{ user_id: string; name: string; offset: number; length: number }> = [];
    const mentionRegex = /@(\w+)/g;
    let match: RegExpExecArray | null;

    while ((match = mentionRegex.exec(text)) !== null) {
      const matchedName = match[1];
      const matchIndex = match.index;
      const matchLength = match[0].length;
      const member = mentionableMembers.find(
        (m) => m.firstName.toLowerCase() === matchedName.toLowerCase()
      );
      if (member) {
        mentions.push({
          user_id: member.id,
          name: member.firstName,
          offset: matchIndex,
          length: matchLength,
        });
      }
    }

    return mentions;
  }, [mentionableMembers]);

  // Highlight @familypal mentions in plain text (no structured mention data)
  const highlightFamilypalMentions = useCallback((text: string): React.ReactNode => {
    const regex = /@familypal/gi;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      parts.push(
        <Box
          key={`fp-${match.index}`}
          component="span"
          sx={{
            color: '#5C6BC0',
            fontWeight: 600,
            bgcolor: 'rgba(92, 107, 192, 0.1)',
            borderRadius: 0.5,
            px: 0.25,
          }}
        >
          {match[0]}
        </Box>
      );
      lastIndex = match.index + match[0].length;
    }

    if (parts.length === 0) return text;

    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  }, []);

  // Render text with mentions highlighted
  const renderTextWithMentions = useCallback((text: string, mentions?: Array<{ user_id: string; name?: string; offset: number; length: number }>) => {
    if (!mentions || mentions.length === 0) {
      return highlightFamilypalMentions(text);
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    // Sort mentions by offset
    const sortedMentions = [...mentions].sort((a, b) => a.offset - b.offset);

    sortedMentions.forEach((mention, i) => {
      // Add text before this mention
      if (mention.offset > lastIndex) {
        parts.push(text.slice(lastIndex, mention.offset));
      }

      // Add the highlighted mention
      parts.push(
        <Box
          key={`mention-${i}`}
          component="span"
          sx={{
            color: 'primary.main',
            fontWeight: 500,
            bgcolor: 'primary.50',
            borderRadius: 0.5,
            px: 0.25,
          }}
        >
          {text.slice(mention.offset, mention.offset + mention.length)}
        </Box>
      );

      lastIndex = mention.offset + mention.length;
    });

    // Add remaining text after last mention
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }

    return parts;
  }, [highlightFamilypalMentions]);

  // Upload files and send message
  const handleSendMessage = async () => {
    if (!selectedThreadId || sending) return;
    if (!messageInput.trim() && pendingFiles.length === 0) return;

    const content = messageInput.trim();
    const currentReplyTo = replyTo;
    setMessageInput('');
    setReplyTo(null);

    try {
      // Upload pending files first
      const attachmentIds: string[] = [];

      if (pendingFiles.length > 0) {
        for (const pendingFile of pendingFiles) {
          if (pendingFile.uploadedId) {
            attachmentIds.push(pendingFile.uploadedId);
            continue;
          }

          setPendingFiles((prev) =>
            prev.map((f) => (f.id === pendingFile.id ? { ...f, uploading: true } : f))
          );

          try {
            // Create a data URL for upload
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(pendingFile.file);
            });

            const uploaded = await uploadChatFile(
              {
                uri: dataUrl,
                name: pendingFile.file.name,
                mimeType: pendingFile.file.type,
                size: pendingFile.file.size,
              },
              (progress) => {
                setPendingFiles((prev) =>
                  prev.map((f) => (f.id === pendingFile.id ? { ...f, progress } : f))
                );
              }
            );

            attachmentIds.push(uploaded.id);

            setPendingFiles((prev) =>
              prev.map((f) =>
                f.id === pendingFile.id ? { ...f, uploading: false, uploadedId: uploaded.id } : f
              )
            );
          } catch (err) {
            console.error('Failed to upload file:', err);
            setPendingFiles((prev) =>
              prev.map((f) =>
                f.id === pendingFile.id ? { ...f, uploading: false, error: 'Upload failed' } : f
              )
            );
            enqueueSnackbar(`Failed to upload ${pendingFile.file.name}`, { variant: 'error' });
          }
        }
      }

      // Parse mentions and send message
      const mentions = parseMentions(content);
      await sendMessage(
        selectedThreadId,
        content,
        attachmentIds.length > 0 ? attachmentIds : undefined,
        mentions.length > 0 ? mentions : undefined,
        currentReplyTo?.id || undefined
      );

      // Clear pending files after successful send
      pendingFiles.forEach((f) => {
        if (f.preview) URL.revokeObjectURL(f.preview);
      });
      setPendingFiles([]);
    } catch (err) {
      setMessageInput(content);
      enqueueSnackbar('Failed to send message', { variant: 'error' });
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Thread icon based on type
  const getThreadIcon = (type: string) => {
    switch (type) {
      case 'family':
        return <ForumIcon />;
      case 'direct':
        return <PersonIcon />;
      case 'group':
        return <GroupIcon />;
      default:
        return <MessageIcon />;
    }
  };

  if (threadsLoading && threads.length === 0) {
    return <LoadingSpinner message="Loading messages..." />;
  }

  // Thread list content (inline to avoid re-render issues)
  const threadListContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2 }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search conversations..." />
      </Box>

      <Divider />

      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {filteredThreads.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              {search ? 'No conversations found' : 'No conversations yet'}
            </Typography>
            {!search && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateFamilyChat}
                disabled={creatingChat}
                sx={{ mt: 2 }}
              >
                {creatingChat ? 'Creating...' : 'Start Family Chat'}
              </Button>
            )}
          </Box>
        ) : (
          filteredThreads.map((thread) => {
            const participants = getThreadParticipants(thread);
            const isSelected = thread.id === selectedThreadId;
            const lastMessage = thread.last_message;
            const unread = thread.unread_count || 0;

            return (
              <ListItemButton
                key={thread.id}
                selected={isSelected}
                onClick={() => { setReplyTo(null); selectThread(thread.id); }}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    bgcolor: 'action.selected',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  {participants.length > 1 ? (
                    <AvatarGroup max={2} sx={{ '& .MuiAvatar-root': { width: 32, height: 32, fontSize: '0.75rem' } }}>
                      {participants.map((p: any) => (
                        <MemberAvatar
                          key={p.id}
                          profilePhotoId={getMemberPhotoId(p.id)}
                          name={getMemberName(p.id)}
                          color={getMemberColor(p.id)}
                        />
                      ))}
                    </AvatarGroup>
                  ) : (
                    <Badge
                      color="success"
                      variant="dot"
                      invisible
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    >
                      <MemberAvatar
                        profilePhotoId={participants[0] ? getMemberPhotoId(participants[0].id) : undefined}
                        name={participants[0] ? getMemberName(participants[0].id) : ''}
                        color={participants[0] ? getMemberColor(participants[0].id) : colors.messages}
                      >
                        {!participants[0] && getThreadIcon(thread.type)}
                      </MemberAvatar>
                    </Badge>
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={unread > 0 ? 600 : 400} noWrap>
                        {getThreadName(thread)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {lastMessage ? formatTime(lastMessage.created_at || lastMessage.timestamp) : ''}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography
                        variant="caption"
                        color={unread > 0 ? 'text.primary' : 'text.secondary'}
                        fontWeight={unread > 0 ? 500 : 400}
                        noWrap
                        sx={{ maxWidth: 180 }}
                      >
                        {lastMessage?.text ||
                          (lastMessage?.attachments?.length > 0
                            ? `📎 ${lastMessage.attachments.length} attachment${lastMessage.attachments.length > 1 ? 's' : ''}`
                            : lastMessage
                              ? 'Message'
                              : 'No messages yet')}
                      </Typography>
                      {unread > 0 && (
                        <Chip
                          label={unread}
                          size="small"
                          color="primary"
                          sx={{ height: 20, minWidth: 20, '& .MuiChip-label': { px: 0.5 } }}
                        />
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            );
          })
        )}
      </List>
    </Box>
  );

  // Chat view content (inline to avoid re-render issues with input focus)
  const chatViewContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'background.paper',
        }}
      >
        {isMobile && (
          <IconButton onClick={() => selectThread(null)} size="small">
            <BackIcon />
          </IconButton>
        )}
        {selectedThread && (
          <>
            <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
              {getThreadName(selectedThread)}
            </Typography>
            <IconButton size="small" onClick={handleOpenMembersDrawer} title="View members">
              <GroupIcon />
            </IconButton>
          </>
        )}
      </Box>

      {/* Messages - Mattermost style */}
      <Box
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}
      >
        {messagesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <LoadingSpinner message="Loading messages..." />
          </Box>
        ) : messages.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="text.secondary">No messages yet. Start the conversation!</Typography>
          </Box>
        ) : (
          <Box sx={{ py: 1, mt: 'auto' }}>
            {loadingMoreMessages && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {messages.map((message, index) => {
              const senderId = message.sender_id;
              const isBot = isBotMessage(senderId);
              const senderName = isBot ? FAMILYPAL_BOT_DISPLAY_NAME : getMemberName(senderId);
              const senderColor = isBot ? '#3949AB' : getMemberColor(senderId);
              const senderPhotoId = isBot ? undefined : getMemberPhotoId(senderId);

              // Mattermost: show avatar/name only for first message in a sequence from same user
              const isConsecutive = index > 0 && messages[index - 1]?.sender_id === senderId;
              const showHeader = !isConsecutive;

              // Look up the replied-to message for context
              const repliedMessage = message.reply_to
                ? messages.find((m) => m.id === message.reply_to)
                : undefined;
              const repliedSenderName = repliedMessage
                ? (isBotMessage(repliedMessage.sender_id) ? FAMILYPAL_BOT_DISPLAY_NAME : getMemberName(repliedMessage.sender_id))
                : undefined;

              return (
                <Box
                  key={message.id}
                  id={`msg-${message.id}`}
                  sx={{
                    display: 'flex',
                    px: 2,
                    py: showHeader ? 1 : 0.25,
                    bgcolor: isBot ? '#E8EAF6' : 'transparent',
                    position: 'relative',
                    transition: 'background-color 0.3s',
                    '&:hover': {
                      bgcolor: isBot ? '#D1D4F0' : 'action.hover',
                    },
                    '&:hover .reply-action': {
                      opacity: 1,
                    },
                  }}
                >
                  {/* Hover reply button */}
                  <IconButton
                    className="reply-action"
                    size="small"
                    onClick={() => {
                      setReplyTo({
                        id: message.id,
                        senderName,
                        content: message.content?.slice(0, 100) || '',
                      });
                      inputRef.current?.focus();
                    }}
                    sx={{
                      position: 'absolute',
                      top: 4,
                      right: 8,
                      opacity: 0,
                      transition: 'opacity 0.15s',
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      boxShadow: 1,
                      zIndex: 1,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <ReplyIcon sx={{ fontSize: 16 }} />
                  </IconButton>

                  {/* Avatar column - fixed width like Mattermost */}
                  <Box sx={{ width: 52, flexShrink: 0, pt: showHeader ? 0.5 : 0 }}>
                    {showHeader && (
                      isBot ? (
                        <BotPostAvatar />
                      ) : (
                        <PostAvatar
                          profilePhotoId={senderPhotoId}
                          name={senderName}
                          color={senderColor}
                        />
                      )
                    )}
                  </Box>

                  {/* Content column */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Reply context */}
                    {repliedMessage && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.5,
                          pl: 1,
                          borderLeft: 3,
                          borderColor: 'primary.main',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          borderRadius: '0 4px 4px 0',
                          py: 0.25,
                        }}
                        onClick={() => {
                          // Scroll to the replied message
                          const el = document.getElementById(`msg-${repliedMessage.id}`);
                          if (el) {
                            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            el.style.backgroundColor = 'rgba(25, 118, 210, 0.08)';
                            setTimeout(() => { el.style.backgroundColor = ''; }, 1500);
                          }
                        }}
                      >
                        <ReplyIcon sx={{ fontSize: 14, color: 'text.secondary', transform: 'scaleX(-1)' }} />
                        <Typography variant="caption" fontWeight={600} color="primary.main">
                          {repliedSenderName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 300 }}>
                          {repliedMessage.content?.slice(0, 80) || 'attachment'}
                          {(repliedMessage.content?.length || 0) > 80 ? '...' : ''}
                        </Typography>
                      </Box>
                    )}

                    {/* Header row with name and time */}
                    {showHeader && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isBot ? 700 : 600,
                            color: senderColor,
                            cursor: isBot ? 'default' : 'pointer',
                            '&:hover': {
                              textDecoration: isBot ? 'none' : 'underline',
                            },
                          }}
                        >
                          {senderName}
                        </Typography>
                        {isBot && (
                          <Chip
                            label="AI"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.625rem',
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              bgcolor: '#5C6BC0',
                              color: '#FFFFFF',
                              '& .MuiChip-label': { px: 0.75 },
                            }}
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatMessageTime(message.created_at)}
                        </Typography>
                      </Box>
                    )}

                    {/* Message content */}
                    {message.content && (
                      <Typography
                        variant="body2"
                        component="div"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          color: isBot ? '#1A237E' : 'text.primary',
                          lineHeight: 1.5,
                        }}
                      >
                        {renderTextWithMentions(message.content, message.mentions)}
                      </Typography>
                    )}

                    {/* Attachments */}
                    {(message.attachments?.length ?? 0) > 0 && (
                      <Box
                        sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: message.content ? 0.5 : 0 }}
                        onContextMenu={(e: React.MouseEvent) => handleMessageContextMenu(e, message)}
                      >
                        {message.attachments!.map((att: any) => {
                          const miniPreviewBg = att.miniPreview || att.mini_preview
                            ? `url(data:image/jpeg;base64,${att.miniPreview || att.mini_preview})`
                            : undefined;
                          if (att.type === 'image') {
                            return (
                              <Box
                                key={att.id}
                                component="img"
                                src={att.thumbnailUri || att.thumbnail_uri || att.uri}
                                alt={att.name || 'Image'}
                                sx={{
                                  maxWidth: 280,
                                  maxHeight: 200,
                                  borderRadius: 1,
                                  cursor: 'pointer',
                                  objectFit: 'cover',
                                  backgroundImage: miniPreviewBg,
                                  backgroundSize: 'cover',
                                }}
                                onClick={() => window.open(att.uri, '_blank')}
                              />
                            );
                          }
                          if (att.type === 'video') {
                            return (
                              <Box
                                key={att.id}
                                sx={{
                                  position: 'relative',
                                  maxWidth: 280,
                                  cursor: 'pointer',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                }}
                                onClick={() => window.open(att.uri, '_blank')}
                              >
                                {att.thumbnailUri || att.thumbnail_uri ? (
                                  <Box
                                    component="img"
                                    src={att.thumbnailUri || att.thumbnail_uri}
                                    sx={{ width: '100%', maxHeight: 200, objectFit: 'cover', backgroundImage: miniPreviewBg, backgroundSize: 'cover' }}
                                  />
                                ) : miniPreviewBg ? (
                                  <Box sx={{ width: 200, height: 120, backgroundImage: miniPreviewBg, backgroundSize: 'cover', filter: 'blur(4px)' }} />
                                ) : (
                                  <Box sx={{ width: 200, height: 120, bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <PlayIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                                  </Box>
                                )}
                                <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                                  <PlayIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.9)' }} />
                                </Box>
                              </Box>
                            );
                          }
                          // File attachment
                          return (
                            <Box
                              key={att.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1,
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                cursor: 'pointer',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              onClick={() => window.open(att.uri, '_blank')}
                              onContextMenu={(e: React.MouseEvent) => handleMessageContextMenu(e, message)}
                            >
                              <FileIcon color="action" fontSize="small" />
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {att.name || 'File'}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Pending files preview */}
      {pendingFiles.length > 0 && (
        <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {pendingFiles.map((file) => (
            <Box
              key={file.id}
              sx={{
                position: 'relative',
                width: 80,
                height: 80,
                borderRadius: 1,
                overflow: 'hidden',
                border: 1,
                borderColor: file.error ? 'error.main' : 'divider',
              }}
            >
              {file.preview ? (
                <img
                  src={file.preview}
                  alt={file.file.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.100',
                  }}
                >
                  <FileIcon color="action" />
                </Box>
              )}
              {file.uploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={24} variant="determinate" value={file.progress} sx={{ color: 'white' }} />
                </Box>
              )}
              <IconButton
                size="small"
                onClick={() => handleRemoveFile(file.id)}
                sx={{
                  position: 'absolute',
                  top: 2,
                  right: 2,
                  bgcolor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  p: 0.25,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      {/* Reply indicator */}
      {replyTo && (
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ReplyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" fontWeight={600} color="primary.main">
              Replying to {replyTo.senderName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {replyTo.content || 'attachment'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)}>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* Message input - Mattermost style */}
      <Box sx={{ p: 2, borderTop: replyTo ? 0 : 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          inputRef={inputRef}
          value={messageInput}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={`Message ${selectedThread ? getThreadName(selectedThread) : ''}`}
          variant="outlined"
          size="small"
          disabled={sending}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: 'background.default',
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" sx={{ mr: 0.5 }} onClick={handleEmojiClick}>
                  <EmojiIcon />
                </IconButton>
                <IconButton size="small" sx={{ mr: 0.5 }} onClick={handleAttachClick}>
                  <AttachIcon />
                </IconButton>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={(!messageInput.trim() && pendingFiles.length === 0) || sending}
                >
                  <SendIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          multiple
          accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
          style={{ display: 'none' }}
        />
      </Box>

      {/* Emoji Picker Popover */}
      <Popover
        open={emojiPickerOpen}
        anchorEl={emojiAnchorEl}
        onClose={handleEmojiClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      >
        <Picker data={data} onEmojiSelect={handleEmojiSelect} theme="light" />
      </Popover>

      {/* Mention Autocomplete Popover */}
      <Popover
        open={mentionPickerOpen && filteredMentionMembers.length > 0}
        anchorEl={mentionAnchorEl}
        onClose={handleCloseMentionPicker}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableAutoFocus
        disableEnforceFocus
      >
        <List dense sx={{ minWidth: 200, maxHeight: 200, overflow: 'auto' }}>
          {filteredMentionMembers.map((member) => (
            <ListItemButton
              key={member.id}
              onClick={() => handleSelectMention(member)}
              sx={{ py: 0.5 }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>
                <Avatar sx={{ width: 24, height: 24, fontSize: 12, bgcolor: colors.primary }}>
                  {member.firstName[0]}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={member.name}
                primaryTypographyProps={{ variant: 'body2' }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 180px)' }}>
      <PageHeader title="Messages" subtitle="Chat with your family" />

      <Card sx={{ height: 'calc(100% - 60px)', overflow: 'hidden' }}>
        <Grid container sx={{ height: '100%' }}>
          {(!isMobile || !selectedThreadId) && (
            <Grid
              size={{ xs: 12, md: 4 }}
              sx={{
                height: '100%',
                borderRight: { md: 1 },
                borderColor: { md: 'divider' },
              }}
            >
              {threadListContent}
            </Grid>
          )}

          {(!isMobile || selectedThreadId) && (
            <Grid size={{ xs: 12, md: 8 }} sx={{ height: '100%' }}>
              {selectedThreadId ? (
                chatViewContent
              ) : (
                <Box
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: 'text.secondary',
                  }}
                >
                  <MessageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="h6">Select a conversation</Typography>
                  <Typography variant="body2">
                    Choose a conversation from the list to start chatting
                  </Typography>
                </Box>
              )}
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Members Drawer */}
      <Drawer
        anchor="right"
        open={membersDrawerOpen}
        onClose={handleCloseMembersDrawer}
        PaperProps={{ sx: { width: 340 } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
            Members
          </Typography>
          <IconButton size="small" onClick={handleCloseMembersDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>

        {participantsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* Current participants */}
            <List sx={{ px: 1 }}>
              {participants.map((p) => {
                const name = getMemberName(p.user_id);
                const isSelf = p.user_id === user?.id;
                const canRemove = isSelf || (canManageMembers && p.role !== 'owner');
                const isDirectChat = selectedThread?.type === 'direct';

                return (
                  <ListItem
                    key={p.user_id}
                    secondaryAction={
                      !isDirectChat && canRemove ? (
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveParticipant(p.user_id)}
                          title={isSelf ? 'Leave chat' : `Remove ${name}`}
                        >
                          <PersonRemoveIcon fontSize="small" />
                        </IconButton>
                      ) : undefined
                    }
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <MemberAvatar
                        profilePhotoId={getMemberPhotoId(p.user_id)}
                        name={name}
                        color={getMemberColor(p.user_id)}
                        sx={{ width: 32, height: 32, fontSize: '0.875rem' }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {name}{isSelf ? ' (you)' : ''}
                          </Typography>
                          {p.role === 'owner' && (
                            <Chip label="Owner" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                          {p.role === 'admin' && (
                            <Chip label="Admin" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>

            {/* Add members section - only for non-direct chats */}
            {selectedThread?.type !== 'direct' && (
              <>
                <Divider />
                {!addMembersMode ? (
                  <Box sx={{ p: 2 }}>
                    <Button
                      startIcon={<PersonAddIcon />}
                      onClick={() => setAddMembersMode(true)}
                      disabled={availableMembers.length === 0}
                      fullWidth
                      variant="outlined"
                      size="small"
                    >
                      {availableMembers.length === 0 ? 'All family members added' : 'Add Members'}
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Add family members
                    </Typography>
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {availableMembers.map((m) => {
                        const userId = getUserId(m)!;
                        const name = `${m.person.first_name} ${m.person.last_name}`.trim();
                        return (
                          <ListItemButton key={userId} onClick={() => toggleNewMember(userId)} dense>
                            <Checkbox
                              edge="start"
                              checked={selectedNewMembers.includes(userId)}
                              size="small"
                              sx={{ mr: 1 }}
                            />
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <MemberAvatar
                                profilePhotoId={m.person.profile_photo_id}
                                name={name}
                                color={m.color || '#96D0F0'}
                                sx={{ width: 28, height: 28, fontSize: '0.75rem' }}
                              />
                            </ListItemIcon>
                            <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2' }} />
                          </ListItemButton>
                        );
                      })}
                    </List>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      <Button
                        size="small"
                        onClick={() => { setAddMembersMode(false); setSelectedNewMembers([]); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleAddMembers}
                        disabled={selectedNewMembers.length === 0 || addingMembers}
                      >
                        {addingMembers ? 'Adding...' : `Add (${selectedNewMembers.length})`}
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Drawer>

      {/* Attachment Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        {contextMenu?.message?.attachments?.some((a: any) => a.type === 'image' || a.type === 'video') && (
          <MenuItem onClick={handleSaveToAlbumClick}>
            <ListItemIcon><AlbumIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Save to Album</ListItemText>
          </MenuItem>
        )}
        {contextMenu?.message?.attachments?.some((a: any) => a.type === 'file') && (
          <MenuItem onClick={handleSaveToDocumentsClick}>
            <ListItemIcon><DocIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Save to Documents</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Album Picker Dialog */}
      <Dialog open={albumPickerOpen} onClose={() => { setAlbumPickerOpen(false); setSavedMessage(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Save to Album</DialogTitle>
        <DialogContent dividers>
          {/* New album */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="New album name"
              value={newAlbumName}
              onChange={(e) => setNewAlbumName(e.target.value)}
            />
            <Button variant="contained" size="small" disabled={!newAlbumName.trim()} onClick={handleCreateAlbumAndSave}>
              Create & Save
            </Button>
          </Box>
          <Divider sx={{ mb: 1 }} />
          <List dense>
            {((albumsData as any)?.family_data || [])
              .filter((a: any) => a.family_id === currentFamily?.id)
              .map((album: any) => (
                <ListItemButton key={album.id} onClick={() => handleSaveToAlbum(album.data?.id)}>
                  <ListItemAvatar>
                    <Avatar variant="rounded" sx={{ bgcolor: 'grey.200' }}>
                      <AlbumIcon color="action" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={album.data?.data?.name || 'Untitled'}
                    secondary={`${album.data?.data_attachments_aggregate?.aggregate?.count || 0} items`}
                  />
                </ListItemButton>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAlbumPickerOpen(false); setSavedMessage(null); }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Document Save Dialog */}
      <Dialog open={docSaveOpen} onClose={() => { setDocSaveOpen(false); setSavedMessage(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>Save to Documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {savedMessage?.attachments?.filter((a: any) => a.type === 'file').length || 0} file(s) will be saved
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select value={docCategory} label="Category" onChange={(e) => setDocCategory(e.target.value)}>
              <MenuItem value="id_legal">ID & Legal</MenuItem>
              <MenuItem value="medical">Medical</MenuItem>
              <MenuItem value="school">School</MenuItem>
              <MenuItem value="financial">Financial</MenuItem>
              <MenuItem value="insurance">Insurance</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDocSaveOpen(false); setSavedMessage(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDocuments}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MessagesPage;
